import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Grid, Paper, Card, CardContent,
  Avatar, Chip, CircularProgress, Alert, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButtonGroup, ToggleButton, Tooltip
} from '@mui/material';
import {
  TrendingUp, CheckCircle, HourglassEmpty, Warning,
  People, FolderOpen, BarChart as BarChartIcon, ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Legend, PieChart, Pie, Cell,
  LineChart, Line, Area, AreaChart
} from 'recharts';
import api from '../services/api';

const COLORS = {
  completed: '#22c55e',
  in_progress: '#3b82f6',
  not_started: '#94a3b8',
  overdue: '#ef4444',
  low: '#22c55e',
  medium: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444',
};

const STATUS_LABELS = { completed: 'Terminées', in_progress: 'En cours', not_started: 'Non démarrées' };

function StatCard({ icon, label, value, color, sub }) {
  return (
    <Card elevation={2} sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ bgcolor: color + '.main', width: 52, height: 52 }}>{icon}</Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">{value}</Typography>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          {sub && <Typography variant="caption" color={color + '.main'}>{sub}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'divider' }} elevation={3}>
      <Typography variant="caption" fontWeight="bold">{label}</Typography>
      {payload.map((p, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color }} />
          <Typography variant="caption">{p.name} : <strong>{p.value}</strong></Typography>
        </Box>
      ))}
    </Paper>
  );
};

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    api.get('/analytics/overview')
      .then(r => setData(r.data))
      .catch(() => setError('Impossible de charger les données analytics.'))
      .finally(() => setLoading(false));
  }, []);

  // Combiner les données créées/complétées sur une timeline des 30 derniers jours
  const timelineData = useMemo(() => {
    if (!data) return [];
    const days = period === '7d' ? 7 : 30;
    const result = [];
    const now = new Date();
    const createdMap = Object.fromEntries((data.tasksCreatedPerDay || []).map(r => [r.date?.split('T')[0], r.count]));
    const completedMap = Object.fromEntries((data.tasksCompletedPerDay || []).map(r => [r.date?.split('T')[0], r.count]));
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(now.getDate() - i);
      const key = d.toISOString().split('T')[0];
      const label = d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      result.push({ date: key, label, créées: createdMap[key] || 0, complétées: completedMap[key] || 0 });
    }
    return result;
  }, [data, period]);

  // Données statut tâches pour pie
  const statusData = useMemo(() => {
    if (!data?.tasksByStatus) return [];
    return data.tasksByStatus.map(r => ({
      name: STATUS_LABELS[r.status] || r.status,
      value: Number(r.count),
      color: COLORS[r.status] || '#94a3b8'
    }));
  }, [data]);

  // Données priorité pour bar
  const priorityData = useMemo(() => {
    if (!data?.tasksByPriority) return [];
    const order = ['low', 'medium', 'high', 'urgent'];
    const labels = { low: 'Basse', medium: 'Moyenne', high: 'Haute', urgent: 'Urgente' };
    return order
      .map(p => ({ name: labels[p], value: Number(data.tasksByPriority.find(r => r.priority === p)?.count || 0), color: COLORS[p] }))
      .filter(r => r.value > 0);
  }, [data]);

  // Données projets pour progress bars
  const projectsData = useMemo(() => {
    if (!data?.projects) return [];
    return data.projects.map(p => ({ ...p, progress: Number(p.progress || 0) }));
  }, [data]);

  // Stats globales
  const totals = useMemo(() => {
    if (!data) return {};
    const byStatus = Object.fromEntries((data.tasksByStatus || []).map(r => [r.status, Number(r.count)]));
    const total = Object.values(byStatus).reduce((a, b) => a + b, 0);
    return {
      total,
      completed: byStatus.completed || 0,
      inProgress: byStatus.in_progress || 0,
      notStarted: byStatus.not_started || 0,
      projects: data.projects?.length || 0,
      overdue: data.overdueTasks?.length || 0,
      completionRate: total > 0 ? Math.round((byStatus.completed || 0) / total * 100) : 0,
    };
  }, [data]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress size={60} /></Box>;
  if (error) return <Container sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Container>;

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 6 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Avatar sx={{ bgcolor: 'primary.main', cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <ArrowBack />
        </Avatar>
        <Box>
          <Typography variant="h4" fontWeight="bold">Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Vue d'ensemble de votre activité et de vos projets</Typography>
        </Box>
      </Box>

      {/* ── KPI Cards ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<FolderOpen />} label="Projets actifs" value={totals.projects} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<CheckCircle />} label="Tâches terminées" value={totals.completed}
            color="success" sub={`${totals.completionRate}% du total`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<HourglassEmpty />} label="En cours" value={totals.inProgress} color="info" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard icon={<Warning />} label="En retard" value={totals.overdue} color="error" />
        </Grid>
      </Grid>

      {/* ── Taux de complétion global ── */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight="bold">Taux de complétion global</Typography>
          <Typography variant="h4" fontWeight="bold" color="success.main">{totals.completionRate}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={totals.completionRate}
          sx={{ height: 12, borderRadius: 6, bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': { borderRadius: 6, bgcolor: totals.completionRate > 70 ? 'success.main' : totals.completionRate > 40 ? 'warning.main' : 'error.main' } }}
        />
        <Box sx={{ display: 'flex', gap: 3, mt: 1.5 }}>
          {statusData.map(s => (
            <Box key={s.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
              <Typography variant="caption" color="text.secondary">{s.name} ({s.value})</Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      <Grid container spacing={3}>

        {/* ── Activité Timeline ── */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight="bold">Activité — Tâches créées vs complétées</Typography>
              <ToggleButtonGroup size="small" value={period} exclusive onChange={(_, v) => v && setPeriod(v)}>
                <ToggleButton value="7d">7 jours</ToggleButton>
                <ToggleButton value="30d">30 jours</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={timelineData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <defs>
                  <linearGradient id="colorCreees" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorComplete" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={period === '7d' ? 0 : 4} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <RTooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="créées" stroke="#3b82f6" strokeWidth={2} fill="url(#colorCreees)" dot={false} />
                <Area type="monotone" dataKey="complétées" stroke="#22c55e" strokeWidth={2} fill="url(#colorComplete)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* ── Statut tâches (Pie) ── */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Répartition des tâches</Typography>
            {statusData.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
                <Typography color="text.secondary">Aucune tâche</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                    paddingAngle={3} dataKey="value" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}>
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <RTooltip formatter={(v, n) => [v, n]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* ── Priorités (Bar) ── */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Tâches par priorité</Typography>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={priorityData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <RTooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Tâches" radius={[6, 6, 0, 0]}>
                  {priorityData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* ── Progression des projets ── */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Progression des projets</Typography>
            {projectsData.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2 }}>Aucun projet</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                {projectsData.map(p => (
                  <Box key={p.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: p.color || 'primary.main' }} />
                        <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 220 }}>{p.title}</Typography>
                        <Chip label={p.status} size="small" sx={{ height: 18, fontSize: '0.65rem' }}
                          color={p.status === 'active' ? 'success' : 'default'} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">{p.completed_tasks}/{p.total_tasks} tâches</Typography>
                        <Typography variant="body2" fontWeight="bold" color={p.progress >= 70 ? 'success.main' : p.progress >= 40 ? 'warning.main' : 'text.secondary'}>
                          {p.progress}%
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title={`${p.progress}% complété`} placement="top">
                      <LinearProgress variant="determinate" value={p.progress}
                        sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': { borderRadius: 4,
                            bgcolor: p.progress >= 70 ? 'success.main' : p.progress >= 40 ? 'warning.main' : 'info.main' } }} />
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Performance équipe ── */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <People color="primary" />
              <Typography variant="h6" fontWeight="bold">Performance équipe</Typography>
            </Box>
            {(!data?.teamPerformance?.length) ? (
              <Typography color="text.secondary">Aucune donnée équipe</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {data.teamPerformance.map((m, i) => {
                  const rate = m.assigned > 0 ? Math.round(m.completed / m.assigned * 100) : 0;
                  return (
                    <Box key={m.id}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem',
                            bgcolor: ['primary', 'secondary', 'success', 'warning', 'info'][i % 5] + '.main' }}>
                            {m.full_name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 130 }}>{m.full_name}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">{m.completed}/{m.assigned}</Typography>
                          <Typography variant="caption" fontWeight="bold" color={rate >= 70 ? 'success.main' : rate >= 40 ? 'warning.main' : 'text.secondary'}>
                            {rate}%
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress variant="determinate" value={rate}
                        sx={{ height: 6, borderRadius: 3, bgcolor: 'grey.100',
                          '& .MuiLinearProgress-bar': { borderRadius: 3,
                            bgcolor: rate >= 70 ? 'success.main' : rate >= 40 ? 'warning.main' : 'info.main' } }} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* ── Tâches en retard ── */}
        {data?.overdueTasks?.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'error.light' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Warning color="error" />
                <Typography variant="h6" fontWeight="bold" color="error.main">Tâches en retard</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tâche</TableCell>
                      <TableCell>Projet</TableCell>
                      <TableCell>Échéance</TableCell>
                      <TableCell>Retard</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.overdueTasks.map(t => {
                      const days = Math.floor((new Date() - new Date(t.end_date)) / 86400000);
                      return (
                        <TableRow key={t.id} sx={{ bgcolor: 'error.50' }}>
                          <TableCell><Typography variant="body2" fontWeight="medium">{t.title}</Typography></TableCell>
                          <TableCell><Typography variant="body2" color="text.secondary">{t.project_title}</Typography></TableCell>
                          <TableCell><Typography variant="body2">{new Date(t.end_date).toLocaleDateString('fr-FR')}</Typography></TableCell>
                          <TableCell><Chip label={`${days}j de retard`} size="small" color="error" /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}

        {/* ── Activité hebdomadaire ── */}
        {data?.weeklyActivity?.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>Tâches complétées par semaine (12 semaines)</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.weeklyActivity.map(w => ({
                  semaine: new Date(w.week_start).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
                  complétées: Number(w.completed)
                }))} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="semaine" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <RTooltip content={<CustomTooltip />} />
                  <Bar dataKey="complétées" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        )}

      </Grid>
    </Container>
  );
}
